import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Menu,
  Modal,
  Radio,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rideTypesApi } from "../../api/rideTypes";
import {
  IconArrowsExchange,
  IconChecks,
  IconChevronDown,
  IconInfoCircle,
  IconPhone,
  IconReceiptRefund,
  IconWalletOff,
  IconX,
} from "@tabler/icons-react";
import { TripStatus } from "@safiri/shared";
import { consoleApi, type LatLng } from "../../api/console";
import { errorMessage } from "../../lib/axios";
import { usePermission } from "../../auth/usePermission";
import { useI18n } from "../../i18n/context";
import { formatMoney } from "../../lib/format";

type Action = "cancel" | "reassign" | "complete" | "closeUnpaid" | "refundWallet";

const CANCELLABLE = new Set<string>([
  TripStatus.CREATED,
  TripStatus.SEARCHING,
  TripStatus.ACCEPTED,
  TripStatus.DRIVER_ARRIVED,
  TripStatus.IN_PROGRESS,
  TripStatus.INTERMEDIATE_STOP_ARRIVED,
  TripStatus.INTERMEDIATE_STOP_SKIPPED,
]);
const REASSIGNABLE = new Set<string>([TripStatus.ACCEPTED, TripStatus.DRIVER_ARRIVED]);

export interface ActionableTrip {
  id: string;
  status: string;
  paymentMode?: string | null;
  rideTypeId?: string | null;
  pickup?: Partial<LatLng> | null;
  riderPhone?: string;
  driverPhone?: string;
  driverId?: string | null;
  /** Part wallet / part cash split, when the trip has one. */
  partPayment?: { walletAmount: number; cashAmount: number; walletRefunded: boolean } | null;
}

function distanceKm(a: LatLng, b: LatLng) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Call + intervention actions for one trip. Hidden entirely for roles without
 * trips.intervene; each action is only offered in statuses the backend
 * accepts, and every one asks for a reason (it lands in the audit log).
 */
export function TripActions({
  trip,
  size = "sm",
  onDone,
}: {
  trip: ActionableTrip;
  size?: "xs" | "sm";
  onDone?: () => void;
}) {
  const { t } = useI18n();
  const canIntervene = usePermission("trips.intervene");
  const [action, setAction] = useState<Action | null>(null);

  const canCancel = CANCELLABLE.has(trip.status);
  const canReassign = REASSIGNABLE.has(trip.status) && !!trip.driverId;
  const part = trip.partPayment ?? null;
  const atDestination = trip.status === TripStatus.DESTINATION_ARRIVED;
  // A refunded wallet part can only be closed as unpaid (backend enforces it).
  const canComplete =
    atDestination &&
    (trip.paymentMode === "cash" || trip.paymentMode === "wallet") &&
    !part?.walletRefunded;
  const canCloseUnpaid = atDestination && !!part;
  const canRefundWallet =
    trip.status === TripStatus.COMPLETED && !!part && !part.walletRefunded;

  return (
    <>
      <Group gap={6} wrap="nowrap">
        {trip.driverPhone && (
          <Button
            component="a"
            href={`tel:${trip.driverPhone}`}
            size={size}
            variant="default"
            leftSection={<IconPhone size={14} />}
            aria-label={t("actions.callDriver")}
          >
            {t("actions.driver")}
          </Button>
        )}
        {trip.riderPhone && (
          <Button
            component="a"
            href={`tel:${trip.riderPhone}`}
            size={size}
            variant="default"
            leftSection={<IconPhone size={14} />}
            aria-label={t("actions.callRider")}
          >
            {t("actions.rider")}
          </Button>
        )}
        {canIntervene &&
          (canCancel || canReassign || canComplete || canCloseUnpaid || canRefundWallet) && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button size={size} variant="light" rightSection={<IconChevronDown size={14} />}>
                {t("actions.intervene")}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {canReassign && (
                <Menu.Item leftSection={<IconArrowsExchange size={16} />} onClick={() => setAction("reassign")}>
                  {t("actions.reassign")}
                </Menu.Item>
              )}
              {canComplete && (
                <Menu.Item leftSection={<IconChecks size={16} />} onClick={() => setAction("complete")}>
                  {t("actions.forceComplete")}
                </Menu.Item>
              )}
              {canCloseUnpaid && (
                <Menu.Item leftSection={<IconWalletOff size={16} />} onClick={() => setAction("closeUnpaid")}>
                  {t("actions.closeUnpaid")}
                </Menu.Item>
              )}
              {canRefundWallet && (
                <Menu.Item
                  leftSection={<IconReceiptRefund size={16} />}
                  onClick={() => setAction("refundWallet")}
                >
                  {t("actions.refundWallet")}
                </Menu.Item>
              )}
              {canCancel && (
                <>
                  <Menu.Divider />
                  <Menu.Item color="red" leftSection={<IconX size={16} />} onClick={() => setAction("cancel")}>
                    {t("actions.cancelTrip")}
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {action && (
        <InterventionModal
          action={action}
          trip={trip}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            onDone?.();
          }}
        />
      )}
    </>
  );
}

function InterventionModal({
  action,
  trip,
  onClose,
  onDone,
}: {
  action: Action;
  trip: ActionableTrip;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [fault, setFault] = useState<"rider" | "driver" | "platform">("platform");
  const [target, setTarget] = useState<string>("auto");
  const [keepWallet, setKeepWallet] = useState(false);
  const part = trip.partPayment ?? null;

  const live = useQuery({
    queryKey: ["ops-live"],
    queryFn: consoleApi.live,
    enabled: action === "reassign",
  });

  const rideTypes = useQuery({
    queryKey: ["ride-types", "all"],
    queryFn: () => rideTypesApi.list(),
    enabled: action === "reassign",
  });

  const candidates = useMemo(() => {
    if (!live.data) return [];
    // A driver may take the trip if their ride type is the trip's, or "also
    // serves" it (e.g. XL for a Haraka trip); own ride type is listed first.
    const servesTrip = (rideTypeId?: string | null) =>
      !trip.rideTypeId ||
      rideTypeId === trip.rideTypeId ||
      !!rideTypes.data?.items
        .find((rt) => rt._id === rideTypeId)
        ?.alsoServes?.map(String)
        .includes(trip.rideTypeId);
    const pickup =
      trip.pickup?.lat !== undefined && trip.pickup?.lng !== undefined
        ? { lat: trip.pickup.lat, lng: trip.pickup.lng }
        : null;
    return live.data.drivers
      .filter(
        (d) =>
          d.state === "idle" &&
          !d.stale &&
          !d.lockedForDebt &&
          d.id !== trip.driverId &&
          servesTrip(d.rideTypeId) &&
          d.position,
      )
      .map((d) => ({ ...d, km: pickup && d.position ? distanceKm(pickup, d.position) : null }))
      .sort(
        (a, b) =>
          Number(a.rideTypeId !== trip.rideTypeId) - Number(b.rideTypeId !== trip.rideTypeId) ||
          (a.km ?? 999) - (b.km ?? 999),
      )
      .slice(0, 8);
  }, [live.data, rideTypes.data, trip]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (action === "cancel") return consoleApi.cancelTrip(trip.id, { reason, fault });
      if (action === "reassign")
        return consoleApi.reassignTrip(trip.id, {
          reason,
          driverId: target === "auto" ? undefined : target,
        });
      if (action === "closeUnpaid")
        return consoleApi.closeUnpaidTrip(trip.id, { reason, keepWallet });
      if (action === "refundWallet") return consoleApi.refundTripWallet(trip.id, { reason });
      return consoleApi.forceCompleteTrip(trip.id, { reason });
    },
    onSuccess: (data) => {
      const mode = (data as { mode?: string })?.mode;
      notifications.show({
        color: "teal",
        message:
          action === "cancel"
            ? t("actions.done.cancel")
            : action === "complete"
              ? t("actions.done.complete")
              : action === "closeUnpaid"
                ? t("actions.done.closeUnpaid")
                : action === "refundWallet"
                  ? t("actions.done.refundWallet")
                  : mode === "assigned"
                    ? t("actions.done.assigned")
                    : t("actions.done.searching"),
      });
      qc.invalidateQueries({ queryKey: ["ops-live"] });
      qc.invalidateQueries({ queryKey: ["trip", trip.id] });
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["ops-inbox"] });
      qc.invalidateQueries({ queryKey: ["cashback-trip", trip.id] });
      onDone();
    },
  });

  const title =
    action === "cancel"
      ? t("actions.cancelTrip")
      : action === "reassign"
        ? t("actions.reassign")
        : action === "closeUnpaid"
          ? t("actions.closeUnpaid")
          : action === "refundWallet"
            ? t("actions.refundWallet")
            : t("actions.forceComplete");
  const danger = action === "cancel" || action === "closeUnpaid" || action === "refundWallet";

  return (
    <Modal opened onClose={onClose} title={<Text fw={700}>{title}</Text>} size="lg">
      <Stack>
        <Alert variant="light" color={danger ? "red" : "blue"} icon={<IconInfoCircle />}>
          {t(`actions.explain.${action}`)}
        </Alert>

        {part && (action === "complete" || action === "closeUnpaid" || action === "refundWallet") && (
          // The admin confirms the split as shown; there is never an amount to type.
          <Alert variant="outline" color="gray">
            {t("actions.partSplit", {
              wallet: formatMoney(part.walletAmount),
              cash: formatMoney(part.cashAmount),
            })}
          </Alert>
        )}

        {action === "closeUnpaid" && (
          <Checkbox
            checked={keepWallet}
            onChange={(e) => setKeepWallet(e.currentTarget.checked)}
            label={t("actions.keepWallet")}
            description={t("actions.keepWalletHelp")}
          />
        )}

        {action === "cancel" && (
          <Radio.Group
            label={t("actions.fault")}
            description={t("actions.faultHelp")}
            value={fault}
            onChange={(v) => setFault(v as typeof fault)}
          >
            <Stack gap={6} mt={6}>
              <Radio value="platform" label={t("actions.fault.platform")} />
              <Radio value="rider" label={t("actions.fault.rider")} />
              <Radio value="driver" label={t("actions.fault.driver")} />
            </Stack>
          </Radio.Group>
        )}

        {action === "reassign" && (
          <Radio.Group label={t("actions.newDriver")} value={target} onChange={setTarget}>
            <ScrollArea.Autosize mah={260} mt={6}>
              <Stack gap={6}>
                <Radio value="auto" label={t("actions.autoDispatch")} />
                {live.isLoading && <Text size="sm" c="dimmed">…</Text>}
                {candidates.map((d) => (
                  <Radio
                    key={d.id}
                    value={d.id}
                    label={`${d.name ?? "Driver"} · ${d.plate ?? "—"}${
                      d.km !== null ? ` · ${d.km.toFixed(1)} km` : ""
                    }${d.rating ? ` · ${d.rating.toFixed(1)}★` : ""}`}
                  />
                ))}
                {!live.isLoading && candidates.length === 0 && (
                  <Text size="sm" c="dimmed">
                    {t("actions.noIdleDrivers")}
                  </Text>
                )}
              </Stack>
            </ScrollArea.Autosize>
          </Radio.Group>
        )}

        <Textarea
          label={t("actions.reason")}
          description={t("actions.reasonHelp")}
          required
          minRows={2}
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          data-autofocus
        />

        {mutation.error && (
          <Alert color="red" role="alert">
            {errorMessage(mutation.error)}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("actions.back")}
          </Button>
          <Button
            color={danger ? "red" : undefined}
            loading={mutation.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => mutation.mutate()}
          >
            {t(`actions.confirm.${action}`)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
